import { z } from "zod";
import { competitionConclusionOf, COMPETITION_HOT_THRESHOLD, priceBandOf } from "./screening-conclusion";

const product = z.object({
  name: z.string().max(300).optional(), link: z.string().url().optional(),
  price: z.number().finite().positive().optional(), sold: z.number().finite().nonnegative().optional(),
  currency: z.string().trim().min(1).max(16).optional(),
}).strict();
const sample = z.array(product).min(1).max(20);
const tag = z.object({ tag: z.string().trim().min(1).max(120), zh: z.string().trim().min(1).max(120), polarity: z.enum(["positive", "negative"]) }).strict();

/** 原始读数是唯一输入；不接受模型另写一份竞争/价格结论。 */
export const ScreeningResultSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("competition"), first: sample, second: sample }).strict(),
  z.object({ kind: z.literal("price_band"), first: sample, second: sample }).strict(),
  z.object({ kind: z.literal("review_pain"), products: z.array(z.object({ sourceUrl: z.string().url(), tags: z.array(tag).min(1).max(12) }).strict()).length(5) }).strict().superRefine((value, context) => {
    if (new Set(value.products.map(product => product.sourceUrl)).size !== value.products.length) context.addIssue({ code: "custom", path: ["products"], message: "评价样本必须来自 5 个不同商品，不能重复计算同一个来源" });
  }),
]);
export type ScreeningResult = z.infer<typeof ScreeningResultSchema>;

export function screeningResultSummary(raw: ScreeningResult): string {
  const result = ScreeningResultSchema.parse(raw);
  if (result.kind === "review_pain") {
    const tags = result.products.flatMap((p) => p.tags);
    // Persist every original tag in screeningResult; summary must fit the existing 600-character record field.
    // Keep whole translated entries and make omitted entries explicit rather than truncating a tag mid-word.
    const line = (polarity: "positive" | "negative") => {
      const entries = [...new Set(tags.filter((t) => t.polarity === polarity).map((t) => `${t.tag}（${t.zh}）`))];
      if (!entries.length) return "未读到";
      const kept: string[] = [];
      for (const entry of entries) {
        const candidate = [...kept, entry].join("、");
        const omitted = entries.length - kept.length - 1;
        const note = omitted ? `…（另 ${omitted} 个见原始标签）` : "";
        if (candidate.length + note.length > 294) break;
        kept.push(entry);
      }
      const omitted = entries.length - kept.length;
      return kept.join("、") + (omitted ? `…（另 ${omitted} 个见原始标签）` : "");
    };
    return `正向标签：${line("positive")}\n负向标签：${line("negative")}`;
  }
  if (result.kind === "competition") {
    const values = (items: typeof result.first) => items.flatMap((p) => p.sold === undefined ? [] : [p.sold]);
    const first = values(result.first), second = values(result.second);
    if (!first.length || !second.length) throw new Error("两轮都必须有可判断的销量读数；空结果不能核实。");
    return competitionConclusionOf({ first, second }).headline;
  }
  const products = [...result.first, ...result.second];
  if (!result.first.some((p) => p.price !== undefined) || !result.second.some((p) => p.price !== undefined)) throw new Error("两轮都必须有价格读数。");
  const priced = products.filter((p) => p.price !== undefined);
  const currencies = new Set(priced.map((p) => p.currency?.toUpperCase()));
  if (currencies.size !== 1 || currencies.has(undefined)) throw new Error("请确认样本币种一致，不能混算或省略币种。");
  const band = priceBandOf(priced.map((p) => p.price));
  if (!band) throw new Error("没有可用的价格样本。");
  const currency = [...currencies][0]!;
  const money = (n: number) => `${currency} ${Number(n.toFixed(2))}`;
  const salesRead = priced.filter((p) => p.sold !== undefined).length;
  const hot = priced.filter((p) => p.sold !== undefined && p.sold >= COMPETITION_HOT_THRESHOLD).length;
  return `价格带（P25–P75）：${money(band.band.low)} ～ ${money(band.band.high)}；建议价（中位价）：${money(band.suggested)}（价格样本 ${band.samples} 条，销量已读取 ${salesRead} 条，热卖样本 ${hot} 条）。`;
}
